import { Dispatch } from 'redux';

import escape from 'escape-html';
import { IUserAttr } from '../actions/session';
import * as Actions from '../actions/testResult';
import ReplaceStyleKeywords from '../utils/ReplaceStyleKeywords';
import AbstractScenario from './AbstractScenario';

/** eラーニングテスト結果情報取得シナリオ用パラメータ */
export interface IFetchTestResultParam {
	careerID: string;
	classID: string;
	testID: string;
	thistoryID: string;
	userAttr: IUserAttr;
}

interface IReceivedQuestion {
	questionKind: string;
	radioAnswerInfoList: any[] | null;
	textAnswerInfoList: any[] | null;
	checkAnswerInfoList: any[] | null;
	selectAnswerInfoList: any[] | null;
}

/**
 * テスト結果シナリオファクトリ
 */
export default class TestResultScenarioFactory {
	/**
	 * eラーニング全問一画面テスト結果情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchTestResultSingle(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchTestResultParam
		> {
			protected process(arg: IFetchTestResultParam) {
				const param = {
					accessType: 10,
					careerID: arg.careerID,
					classID: arg.classID,
					displayStyle: 0,
					groupInfoList: [],
					lang: arg.userAttr.lang,
					score: '',
					scoreMessage: '',
					testID: arg.testID,
					thistoryID: arg.thistoryID,
					userId: arg.userAttr.userId,
					viewMode: 0,
				};
				return this.read('el_test_TestResultBlockModel', param).then(
					data => {
						TestResultScenarioFactory.dispatchTestResultParams(
							this.dispatch,
							data
						);
					}
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング一問一画面テスト結果情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchTestResultMultiPage(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchTestResultParam
		> {
			protected process(arg: IFetchTestResultParam) {
				const param = {
					accessType: 10,
					careerID: arg.careerID,
					classID: arg.classID,
					displayStyle: 1,
					lang: arg.userAttr.lang,
					testID: arg.testID,
					thistoryID: arg.thistoryID,
					userId: arg.userAttr.userId,
					viewMode: 0,
				};
				return this.read(
					'el_test_TestStepResultBlockModel',
					param
				).then(data => {
					TestResultScenarioFactory.dispatchTestResultParams(
						this.dispatch,
						data
					);
				});
			}
		}(dispatch, token);
		return scenario;
	}

	protected static dispatchTestResultParams(dispatch: Dispatch, data: any) {
		const testTitle: string = data.testTitle;
		const score: number = data.score;
		const scoreMessage: string = data.scoreMessage;
		const passFlg: boolean = data.passFlg;
		const paperType: number = data.paperType;
		const questionGroupDisplay: boolean = data.questionGroupDisplay;
		const groups = data.groupInfoList.map((g: any) =>
			this.toGroup(g, paperType)
		);
		dispatch(
			Actions.setTestResultParams({
				groups,
				paperType,
				passFlg,
				questionGroupDisplay,
				score,
				scoreMessage,
				testTitle,
			})
		);
	}

	protected static toGroup(receivedGroup: any, paperType: number) {
		const group: Actions.IGroup = {
			correctCount: receivedGroup.correctCount,
			groupTitle: receivedGroup.groupTitle,
			questions: receivedGroup.questionInfoList.map((q: any) =>
				this.toQuestion(q, paperType)
			),
			quizCount: receivedGroup.quizCount,
		};
		return group;
	}

	protected static convertKind({
		questionKind,
		radioAnswerInfoList,
		textAnswerInfoList,
		checkAnswerInfoList,
		selectAnswerInfoList,
	}: IReceivedQuestion) {
		switch (questionKind) {
			case 'radio':
				return {
					answers: radioAnswerInfoList
						? radioAnswerInfoList.map(this.toRadioAnswer)
						: void 0,
					questionKind: Actions.QuestionKind.RADIO,
				} as Pick<Actions.IRadioQuestion, 'answers' | 'questionKind'>;
			case 'text':
				return {
					answers: textAnswerInfoList
						? textAnswerInfoList.map(this.toTextAnswer)
						: void 0,
					questionKind: Actions.QuestionKind.TEXT,
				} as Pick<Actions.ITextQuestion, 'answers' | 'questionKind'>;
			case 'check':
				return {
					answers: checkAnswerInfoList
						? checkAnswerInfoList.map(this.toCheckAnswer)
						: void 0,
					questionKind: Actions.QuestionKind.CHECK,
				} as Pick<Actions.ICheckQuestion, 'answers' | 'questionKind'>;
			case 'select':
				return {
					answers: selectAnswerInfoList
						? selectAnswerInfoList.map(this.toSelectAnswer)
						: void 0,
					questionKind: Actions.QuestionKind.SELECT,
				} as Pick<Actions.ISelectQuestion, 'answers' | 'questionKind'>;
			default:
				throw Error('unknown question kind: ' + questionKind);
		}
	}

	protected static toQuestion(
		receivedQuestion: any,
		paperType: number
	): Actions.IQuestion {
		const base: Actions.IQuestionBase = {
			answerResultMessage: receivedQuestion.answerResultMessage,
			answerResultType: receivedQuestion.answerResultType,
			answerScore: receivedQuestion.answerScore,
			hint: ReplaceStyleKeywords.replaceStyleKeywords(
				receivedQuestion.hint ? escape(receivedQuestion.hint) : ''
			),
			hintFilePath: receivedQuestion.hintFilePath,
			imageFilePath: receivedQuestion.imageFilePath,
			questionID: receivedQuestion.questionID,
			questionNo: receivedQuestion.questionNo,
			questionScore: receivedQuestion.questionScore,
			questionText: ReplaceStyleKeywords.replaceStyleKeywords(
				escape(receivedQuestion.questionText)
			),
		};
		return {
			...base,
			...this.convertKind(receivedQuestion),
		};
	}

	protected static toRadioAnswer(receivedAnswer: any): Actions.IRadioAnswer {
		return {
			displayNum: receivedAnswer.displayNum,
			options: receivedAnswer.answersList.map(
				(receivedOption: Actions.IRadioOption) => ({
					answerFlg: receivedOption.answerFlg,
					answerMessage: receivedOption.answerMessage,
					answerText: receivedOption.answerText,
				})
			),
		};
	}

	protected static toTextAnswer(receivedAnswer: any): Actions.ITextAnswer {
		return {
			answerFlg: receivedAnswer.answerFlg,
			answerMessage: receivedAnswer.answerMessage,
			answerText: receivedAnswer.answerText,
			displayNum: receivedAnswer.displayNum,
		};
	}

	protected static toCheckAnswer(receivedAnswer: any): Actions.ICheckAnswer {
		return {
			answerFlg: receivedAnswer.answerFlg,
			answerText: receivedAnswer.answerText,
			selected: receivedAnswer.selected,
		};
	}

	protected static toSelectAnswer(
		receivedAnswer: any
	): Actions.ISelectAnswer {
		return {
			displayNum: receivedAnswer.displayNum,
			options: receivedAnswer.answersList.map(
				(receivedOption: Actions.ISelectOption) => ({
					answerFlg: receivedOption.answerFlg,
					answerMessage: receivedOption.answerMessage,
					answerText: receivedOption.answerText,
				})
			),
		};
	}
}



// WEBPACK FOOTER //
// ./src/scenario/TestResultScenarioFactory.ts